import {
  EActionCategory,
  ECardinality,
  EDatasourceType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  ISymbol
} from "@kuika/kuika-cl-model";
import type { CascaderOptionType, CascaderValueType } from "antd/es/cascader";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { ActionDefinitionLookup } from "../../../../../shared/utility/action-definition-lookup";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { KModal } from "../../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../../ui-kit/k-modal/k-modal-footer";
import KuikaCollapsePanel from "../../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaModalIconWrapper from "../../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaOverflowText from "../../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaH5 from "../../../../ui-kit/typography/H5/H5";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import { getActionCategoryIconName } from "../../properties-panel/action-category-icons";
import { EActionNodeCategory } from "../../properties-panel/action-picker";
import type {
  IActionPickerOption,
  IActionPickerOptionMeta
} from "../../properties-panel/action-picker-popup/action-picker-node";
import ActionPickerPopup from "../../properties-panel/action-picker-popup/action-picker-popup";
import { SymbolPicker } from "../../properties-panel/symbol-picker";
import "./dynamic-assignment-action-selection.scss";

export interface IDynamicAssignmentActionSelectionState {
  /** The popup's option tree, built when it opens. */
  options: IActionPickerOption[];
  popupVisible: boolean;
  actionPanelActive: boolean;
  dynamicAssignmentActionReturnType: string | undefined;
  fieldName: ISymbol | undefined;
  /** Set while a "Change Action" confirmation is pending; holds the action to apply on Continue. */
  changeConfirmVisible: boolean;
  pendingAction: IAction | null;
}

export interface IDynamicAssignmentActionSelectionProps {
  action: IAction | undefined;
  actionSelectedHandler: (action) => void;
  parameterSetHandler: (parameter, symbol) => void;
}

class DynamicAssignmentActionSelection extends PureSdComponent<
  IDynamicAssignmentActionSelectionProps & IWithSDProps & IWithContextProp,
  IDynamicAssignmentActionSelectionState
> {
  constructor(props: IDynamicAssignmentActionSelectionProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      options: [],
      popupVisible: false,
      actionPanelActive: true,
      fieldName: undefined,
      dynamicAssignmentActionReturnType: "User",
      changeConfirmVisible: false,
      pendingAction: null
    };
  }

  prepareDynamicAssignmentActionField = () => {
    const wf = this.props.sd.selectedWf;
    const step = this.props.sd.findStepRecursively(wf!, (s) => s.id === this.props.sd.selectedWfNode?.id!);
    const lane = wf?.nodes?.lanes.find((l) => l.id === this.props.sd.selectedWfNode?.id);
    if (!step && !lane) {
      return;
    }
    if (lane) {
      this.setState({
        fieldName: lane.assignment.dynamicAssignmentActionField,
        dynamicAssignmentActionReturnType: lane.assignment.dynamicAssignmentActionReturnType ?? "User"
      });
    }
    if (step) {
      this.setState({
        fieldName: step.assignment.dynamicAssignmentActionField,
        dynamicAssignmentActionReturnType: step.assignment.dynamicAssignmentActionReturnType ?? "User"
      });
    }
  };

  componentDidMount = (): void => {
    this.prepareDynamicAssignmentActionField();
  };

  componentDidUpdate = (): void => {
    this.prepareDynamicAssignmentActionField();
  };

  /** Opens and closes the popup. The tree is built on open, so it lists the current catalogue. */
  handlePopupOpenChange = (open: boolean): void => {
    if (open) {
      this.setState({ options: this.generateActionDefinitionItems(), popupVisible: true });
    } else if (this.state.popupVisible) {
      this.setState({ popupVisible: false });
    }
  };

  /** The popup's pick: the same `selectedOptions` path the Cascader's onChange delivered. */
  handlePick = (selectedOptions: CascaderOptionType[]): void => {
    const values: CascaderValueType = selectedOptions.map((option: CascaderOptionType) => option.value ?? "");
    this.handleAddAction(values, selectedOptions);
  };

  actionDefinitionLookup: ActionDefinitionLookup = new ActionDefinitionLookup();

  getActionDefinitionById = (id: string): IActionDefinitionDto | undefined =>
    this.actionDefinitionLookup.get(this.props.sd.actionDefinitions, id);

  generateProcessAutomationSection = (): CascaderOptionType[] => {
    let actionDefinitions = _.clone(this.props.sd.actionDefinitions);
    const setValueAction = actionDefinitions.find((a) => a.id === "f87fdf4c-0b4d-4914-8345-9b02cefe4a43");
    actionDefinitions = actionDefinitions.filter((x) => {
      return x.actionType != EDatasourceType.SystemJSFunctions;
    });
    actionDefinitions = actionDefinitions.filter((x) => {
      return x.isDeprecated !== true;
    });
    actionDefinitions = actionDefinitions.filter((x) => {
      return (
        x.actionCategory === EActionCategory.Custom || x.id === "E77EC949-2D55-4E95-9FB6-02D55FCB19CD".toLowerCase()
      );
    });

    // VTO: add static setting option
    setValueAction && actionDefinitions.push(setValueAction);

    const tmp = actionDefinitions
      .filter((x) => x.actionCategory == EActionCategory.ProcessAutomation)
      .filter((x) => x.actionCategory !== EActionCategory.Other)
      .map((x) => {
        return {
          category: EActionNodeCategory.action,
          label: x.displayName ?? x.name,
          value: x.id,
          children: undefined
        } as CascaderOptionType;
      })
      .sort((n1: CascaderOptionType, n2: CascaderOptionType) => {
        if (n1?.label && n2?.label) {
          if (n1.label > n2.label) {
            return 1;
          }
          if (n1.label < n2.label) {
            return -1;
          }
        }
        return 0;
      });
    return tmp;
  };

  getDataSourceName = (datasourceType: EDatasourceType) => {
    if (datasourceType == EDatasourceType.ManagedDb) {
      return "Managed DB (SQL Server)";
    }

    if (datasourceType == EDatasourceType.ManagedDbOracle) {
      return "Managed DB (Oracle)";
    }

    if (datasourceType == EDatasourceType.ManagedDbPostgre) {
      return "Managed DB (Postgre)";
    }

    return null;
  };

  generateDataSources = (): CascaderOptionType[] => {
    const result: CascaderOptionType[] = [];

    const actionDefinitions = _.clone(this.props.sd.actionDefinitions);
    actionDefinitions.forEach((ad) => {
      if (ad.kDatasource_Name == "SystemJsFunctions") return;
      if (result.filter((x) => x.value?.toString() == ad.kDatasource_Id).length > 0) return;
      if (ad.actionCategory === EActionCategory.ManagedDb) return;

      const item: CascaderOptionType = {
        category: EActionNodeCategory.actionCategory,
        label: this.getDataSourceName(ad.actionType) ?? ad.kDatasource_Name,
        value: ad.kDatasource_Id,
        children: this.generateActionsByDataSource(ad.kDatasource_Id)
      };
      if (item.label && item.label !== "") {
        if (item.children && item.children.length > 0) result.push(item);
      }
    });
    return result;
  };

  generateActionsByDataSource = (datasourceId: string): CascaderOptionType[] => {
    const result: CascaderOptionType[] = [];

    const actionDefinitions = _.clone(this.props.sd.actionDefinitions?.filter((x) => x.kDatasource_Id == datasourceId));

    actionDefinitions
      .filter((x) => !this.props.sd.project.actionFolders?.find((y) => y.actionIds?.includes(x.id)))
      .filter((x) => x.returnTypeName != null)
      .filter(
        (x) =>
          x.returnTypeName == "String" ||
          (x.returnTypeModel?.elements != undefined &&
            x.returnTypeModel.elements.some((e) => e.type.toString() == "String"))
      )
      .forEach((ad) => {
        const item: CascaderOptionType = {
          category: EActionNodeCategory.action,
          label: ad.name,
          value: ad.id,
          children: []
        };
        result.push(item);
      });

    this.props.sd.project.actionFolders
      ?.filter((x) => x.dataSourceType === datasourceId)
      ?.forEach((af) => {
        const item: CascaderOptionType = {
          category: EActionNodeCategory.actionFolder,
          label: af.name,
          value: af.id,
          children: af.actionIds
            ?.filter((x) => this.props.sd.actionDefinitions.find((y) => y.id === x)?.name !== undefined)
            .filter((x) => {
              const a = this.props.sd.actionDefinitions.find((y) => y.id === x);
              if (a) {
                return (
                  a.returnTypeName == "String" ||
                  (a.returnTypeModel?.elements != undefined &&
                    a.returnTypeModel.elements.some((e) => e.type.toString() == "String"))
                );
              }
              return false;
            })
            ?.map((x) => ({
              category: EActionNodeCategory.action,
              label: this.props.sd.actionDefinitions.find((y) => y.id === x)?.name,
              value: x
            }))
        };
        if (item.children && item.children.length > 0) result.push(item);
      });
    return result.sort((n1: CascaderOptionType, n2: CascaderOptionType) => {
      if (n1.category === EActionNodeCategory.actionFolder && n2.category !== EActionNodeCategory.actionFolder) {
        return -1;
      }
      if (n1?.label && n2?.label) {
        const name1 = (n1.label as string).toLowerCase();
        const name2 = KMainFunctions.turkishCharsToEnglish(n2.label as string).toLowerCase();
        if (name1 > name2) {
          return 1;
        }
        if (name1 < name2) {
          return -1;
        }
      }
      return 0;
    });
  };

  /** A top-level category row: its display name is the enum name this picker has always shown. */
  getCategoryPickerMeta = (category: string): IActionPickerOptionMeta => {
    const actionCategory: EActionCategory = Number(category) as EActionCategory;
    return {
      label: EActionCategory[actionCategory],
      iconName: getActionCategoryIconName(actionCategory),
      isCustom: actionCategory === EActionCategory.Custom
    };
  };

  generateActionDefinitionItems = (isInitialAction?: boolean): IActionPickerOption[] => {
    const result: IActionPickerOption[] = [];

    for (const value in EActionCategory) {
      const item: IActionPickerOption = {
        category: value,
        label: EActionCategory[value],
        value: EActionCategory[value],
        pickerMeta: this.getCategoryPickerMeta(value),
        children: []
      };
      switch (Number(value)) {
        case 2:
          item.children = this.generateDataSources();
          // TODO: Currently actions are filtered by String return type (user might be able to select field to bind)
          break;
        case 14:
          item.children = this.generateProcessAutomationSection();
          break;
        default:
          break;
      }
      if (item.children && item.children.length > 0) {
        result.push(item);
      }
    }

    return result.sort((n1: CascaderOptionType, n2: CascaderOptionType) => {
      if (n1?.label && n2?.label) {
        if (n1.label > n2.label) {
          return 1;
        }
        if (n1.label < n2.label) {
          return -1;
        }
      }
      return 0;
    });
  };

  handleAddAction = (value: CascaderValueType, selectedOptions?: CascaderOptionType[] | undefined) => {
    if (!selectedOptions || selectedOptions.length == 0) {
      return;
    }
    const actions = _.clone(this.props.sd.actionDefinitions);
    const actionDef = actions.find((p) => p.name === selectedOptions[selectedOptions.length - 1].label);
    if (actionDef) {
      const actionId = Guid.create();
      const action = {
        id: actionId.toString(),
        actionDefinitionId: actionDef.id,
        label: actionDef.name,
        description: actionDef.name,
        parameters: actionDef.actionParameters.map((x) => this.cloneActionParameter(x)),
        isExpandedPanel: true
      } as unknown as IAction;
      // Changing an already-set action resets the value/settings the user entered, so
      // confirm first. A first-time pick applies immediately.
      if (this.props.action) {
        this.setState({ pendingAction: action, changeConfirmVisible: true });
      } else {
        this.props.actionSelectedHandler(action);
      }
    }
  };

  confirmChangeAction = (): void => {
    const pending: IAction | null = this.state.pendingAction;
    this.setState({ changeConfirmVisible: false, pendingAction: null });
    if (pending) this.props.actionSelectedHandler(pending);
  };

  cancelChangeAction = (): void => {
    this.setState({ changeConfirmVisible: false, pendingAction: null });
  };

  cloneActionParameter = (actionParameter: IActionParameter) => {
    const tmp = _.cloneDeep(actionParameter);
    tmp.symbol = {
      category: ESymbolCategory.fixed,
      fixedValue: ""
    } as ISymbol;
    return tmp;
  };

  onSelectField = (val) => {
    const wf = this.props.sd.selectedWf;
    if (!wf) {
      return;
    }

    const step = this.props.sd.findStepRecursively(wf!, (s) => s.id === this.props.sd.selectedWfNode?.id!);
    const lane = wf?.nodes?.lanes.find((l) => l.id === this.props.sd.selectedWfNode?.id);
    if (!step && !lane) {
      return;
    }
    const selectedField = { category: ESymbolCategory.fixed, fixedValue: val, label: val } as ISymbol;
    if (lane) {
      lane.assignment.dynamicAssignmentActionField = selectedField;
    }
    if (step) {
      step.assignment.dynamicAssignmentActionField = selectedField;
    }
    this.setState({ fieldName: selectedField }, async () =>
      this.props.sd.saveWorkFlow(wf, ESdChangeReason.SaveWorkflow)
    );
  };

  private toggleDynamicAssignmentScope = (val: string) => {
    const wf = this.props.sd.selectedWf;
    if (!wf) {
      return;
    }

    const step = this.props.sd.findStepRecursively(wf!, (s) => s.id === this.props.sd.selectedWfNode?.id!);
    const lane = wf?.nodes?.lanes.find((l) => l.id === this.props.sd.selectedWfNode?.id);
    if (!step && !lane) {
      return;
    }
    if (lane) {
      lane.assignment.dynamicAssignmentActionReturnType = val;
    }
    if (step) {
      step.assignment.dynamicAssignmentActionReturnType = val;
    }
    this.setState({ dynamicAssignmentActionReturnType: val }, async () =>
      this.props.sd.saveWorkFlow(wf, ESdChangeReason.SaveWorkflow)
    );
  };

  private _getParamtersOptionsFromReturnType = () => {
    if (!this.props.action) {
      return <></>;
    }
    const { fieldName } = this.state;
    const actionDefinition = this.props.sd.actionDefinitions.find(
      (a) => a.id === this.props.action?.actionDefinitionId
    );

    const dynamicAssignmentActionReturnType = (
      <div className="">
        <div className="kuika_add_actions_properties_name" style={{ marginBottom: "6px", marginTop: "8px" }}>
          Action returns
        </div>
        <span>
          <KuikaTabs
            options={[
              {
                value: "User",
                label: "Users"
              },
              {
                value: "Role",
                label: "Roles"
              }
            ]}
            onChange={(val) => {
              this.toggleDynamicAssignmentScope(val);
            }}
            value={this.state.dynamicAssignmentActionReturnType}
          />
        </span>
      </div>
    );
    const options: any[] = [];

    if (actionDefinition?.returnTypeName === "String" && actionDefinition?.returnCardinality === ECardinality.One) {
      return dynamicAssignmentActionReturnType;
    }
    actionDefinition?.returnTypeModel?.elements
      ?.sort((a, b) => a.name.localeCompare(b.name))
      .forEach((p) => {
        const type = p.type as any;
        if (p.isList !== true && p.elementTypeModel == null && type === "String") {
          options.push({
            label: p.name,
            value: p.name
          });
        }
      });

    if (!options.some((o) => o.label === fieldName?.fixedValue)) {
      if (options.some((o) => o.label === "UserName")) {
        this.setState({
          fieldName: { category: ESymbolCategory.fixed, fixedValue: "UserName", label: "UserName" } as ISymbol
        });
      } else if (options.some((o) => o.label === "Username")) {
        this.setState({
          fieldName: { category: ESymbolCategory.fixed, fixedValue: "Username", label: "Username" } as ISymbol
        });
      } else {
        this.setState({
          fieldName: {
            category: ESymbolCategory.fixed,
            fixedValue: options[0].label,
            label: options[0].label
          } as ISymbol
        });
      }
    }

    return (
      <div>
        {dynamicAssignmentActionReturnType}
        {/* <div className="">
          <div className="kuika_add_actions_properties_name" style={{ marginBottom: "6px", marginTop: "8px" }}>
            Action returns
          </div>
          <span>
            <KuikaTabs
              options={[
                {
                  value: "User",
                  label: "Users"
                },
                {
                  value: "Role",
                  label: "Roles"
                }
              ]}
              onChange={(val) => {
                this.toggleDynamicAssignmentScope(val);
              }}
              value={this.state.dynamicAssignmentActionReturnType}
            />
          </span>
        </div> */}
        <div className="">
          <div className="kuika_add_actions_properties_name" style={{ marginBottom: "6px", marginTop: "8px" }}>
            Field to get information
          </div>
          <div>
            <KuikaSelectbox
              data-testid="dynamicAssignmentFieldSelect"
              style={{ width: "100%" }}
              value={fieldName?.fixedValue}
              placeholder="Select field"
              onChange={(val) => this.onSelectField(val)}
              className="kuika_add_actions_inputs_size"
              searchable
              size="md"
              options={(options ?? []).map((o): KuikaSelectboxOption => ({
                key: o.label,
                value: o.value,
                label: o.label
              }))}
            />
          </div>
        </div>
      </div>
    );
  };

  render() {
    const randomGuid = Guid.create();
    const { fieldName } = this.state;

    return (
      <div className="kuika_Wrapper">
        <div>
          {
            <KuikaCollapsePanel
              onChange={() => this.setState({ actionPanelActive: !this.state.actionPanelActive })}
              activeKey={this.state.actionPanelActive ? "1" : ""}
              defaultActiveKey={"1"}
              expandIconPosition="right"
              className="kuika-right-panel__collapse"
              style={{ margin: "var(--gap-s) var(--gap-xs) var(--gap-s) var(--gap-s)" }}
              expandIcon={() => (
                <KuikaSvg
                  name="chevron-right"
                  type="arrows"
                  width={16}
                  height={16}
                  className={this.state.actionPanelActive ? "kuika_rotate90deg" : "kuika_rotate0deg"}
                />
              )}
              items={[
                {
                  key: "1",
                  // Same rendering the panel gives a string label, plus a hook for the chosen action.
                  label: (
                    <KuikaOverflowText data-testid="dynamicAssignmentActionLabel">
                      {this.props.action ? this.props.action.label : "Dynamic Assignment"}
                    </KuikaOverflowText>
                  ),
                  forceRender: true,
                  children: (
                    <>
                      {this._getParamtersOptionsFromReturnType()}

                      {this.props.action?.parameters &&
                        this.props.action.parameters.length > 0 &&
                        this.props.action.parameters.map((p, idx) => {
                          return (
                            <div
                              className={""}
                              key={p.name + randomGuid + idx}
                              data-testid={`dynamicAssignmentParameter-${p.name}`}
                            >
                              <span
                                style={{ marginBottom: 6, marginTop: 8 }}
                                className="k_Regular-G1-Left kuika_rightPanelDIName"
                              >
                                {p.name} ({p.Typename})
                              </span>

                              <div className="kuika_styling_typography_wrapper kuika_screen-input__wrapper">
                                <SymbolPicker
                                  data_testid="dynamicAssignmentParameterValue"
                                  changeType="onBlur"
                                  symbol={p.symbol}
                                  valueType={p.Typename}
                                  targetCardinality={p.cardinality}
                                  onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
                                    this.props.parameterSetHandler(p, symbol)
                                  }
                                />
                              </div>
                            </div>
                          );
                        })}
                      <div className="kuika_actionPanelWrapperHeader">
                        <div style={{ display: "flow-root" }}>
                          <ActionPickerPopup
                            projectType={this.props.sd.project?.projectType}
                            options={this.state.options}
                            open={this.state.popupVisible}
                            onOpenChange={this.handlePopupOpenChange}
                            onPick={this.handlePick}
                            getActionDefinition={this.getActionDefinitionById}
                            searchTestId="dynamicAssignmentActionPickerSearch"
                            placeholder="Search any action.."
                          >
                            <span
                              data-testid="dynamicAssignmentActionPickerTrigger"
                              className="kuika_addNavigationLink"
                            >
                              + {this.props.action ? "CHANGE" : "SELECT"} ACTION
                            </span>
                          </ActionPickerPopup>
                        </div>
                      </div>
                    </>
                  )
                }
              ]}
            />
          }
        </div>

        <KModal
          header="Change Action"
          showModal={this.state.changeConfirmVisible}
          closeModal={this.cancelChangeAction}
          width={420}
          footer={
            <KModalFooter
              onCancel={this.cancelChangeAction}
              submitLabel="Continue"
              onSubmit={this.confirmChangeAction}
            />
          }
        >
          <div className="kuika-change-action-confirm">
            <KuikaModalIconWrapper iconName="alert-triangle" intent="warning" />
            <KuikaH5 className="kuika-change-action-confirm__title">Change Action</KuikaH5>
            <KuikaText className="kuika-change-action-confirm__text">
              Changing the action will reset the value and settings you&apos;ve entered. Continue?
            </KuikaText>
          </div>
        </KModal>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.SaveWorkflow ||
      changeReason === ESdChangeReason.WFStepMappingChange ||
      changeReason === ESdChangeReason.ClickWfDesignXmlNode
    ) {
      return true;
    }
    return false;
  };
}

const actionPicker = withContext<IDynamicAssignmentActionSelectionProps>(
  withSD<IDynamicAssignmentActionSelectionProps>(DynamicAssignmentActionSelection)
);
export { actionPicker as DynamicAssignmentActionSelection };
